"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.modifyMultiSig = exports.prepareStorage = exports.getAuthorization = exports.prepareModifyMultiSig = exports.assertValidRequiredSignatures = exports.assertNoDataChange = exports.assertRequiredSignersExceedsSigners = void 0;
const internal_1 = require("../../internal");
const types_1 = require("../../types");
const conversion_1 = require("../../utils/conversion");
const internal_2 = require("../../utils/internal");
/**
 * @hidden
 */
function calculateSignerDelta(current, target) {
    if (!target) {
        return { signersToAdd: [], signersToRemove: [] };
    }
    const currentSet = new Set(current.map(conversion_1.signerToString));
    const targetSet = new Set(target.map(conversion_1.signerToString));
    const newSigners = new Set([...target].filter(s => !currentSet.has((0, conversion_1.signerToString)(s))));
    const removedSigners = new Set([...current].filter(s => !targetSet.has((0, conversion_1.signerToString)(s))));
    return { signersToAdd: Array.from(newSigners), signersToRemove: Array.from(removedSigners) };
}
/**
 * @hidden
 */
function assertRequiredSignersExceedsSigners(currentSignerCount, requiredSignatures, inputSigners, newRequiredSignatures) {
    if ((inputSigners && requiredSignatures.gt(inputSigners.length) && !newRequiredSignatures) ||
        (inputSigners && (newRequiredSignatures === null || newRequiredSignatures === void 0 ? void 0 : newRequiredSignatures.gt(inputSigners.length)))) {
        throw new internal_1.PolymeshError({
            code: types_1.ErrorCode.ValidationError,
            message: 'The number of signers should not be less than the number of required signatures',
        });
    }
    else if (newRequiredSignatures === null || newRequiredSignatures === void 0 ? void 0 : newRequiredSignatures.gt(currentSignerCount)) {
        throw new internal_1.PolymeshError({
            code: types_1.ErrorCode.ValidationError,
            message: 'The number of required signatures should not exceed the number of signers',
        });
    }
}
exports.assertRequiredSignersExceedsSigners = assertRequiredSignersExceedsSigners;
/**
 * @hidden
 */
function assertNoDataChange(requiredSignatures, signersToAdd, signersToRemove, inputSigners, newRequiredSignatures) {
    if (inputSigners && !signersToAdd.length && !signersToRemove.length) {
        throw new internal_1.PolymeshError({
            code: types_1.ErrorCode.NoDataChange,
            message: 'The given signers are equal to the current signers. At least one signer should be added or removed',
        });
    }
    if (newRequiredSignatures && requiredSignatures.eq(newRequiredSignatures)) {
        throw new internal_1.PolymeshError({
            code: types_1.ErrorCode.NoDataChange,
            message: 'The given required signatures are equal to the current required signatures. The number of required signatures should be different',
        });
    }
}
exports.assertNoDataChange = assertNoDataChange;
/**
 * @hidden
 */
function assertValidRequiredSignatures(newRequiredSignatures) {
    if (newRequiredSignatures === null || newRequiredSignatures === void 0 ? void 0 : newRequiredSignatures.lt(1)) {
        throw new internal_1.PolymeshError({
            code: types_1.ErrorCode.ValidationError,
            message: 'The number of required signatures should be at least 1',
        });
    }
}
exports.assertValidRequiredSignatures = assertValidRequiredSignatures;
/**
 * @hidden
 */
function prepareModifyMultiSig(args) {
    return __awaiter(this, void 0, void 0, function* () {
        const { context: { polymeshApi: { tx }, }, storage: { signersToAdd, signersToRemove, requiredSignatures, currentSignerCount }, context, } = this;
        const { signers, multiSig, requiredSignatures: newRequiredSignatures } = args;
        const [signingIdentity, admin] = yield Promise.all([
            context.getSigningIdentity(),
            multiSig.getAdmin(),
        ]);
        if (!(admin === null || admin === void 0 ? void 0 : admin.isEqual(signingIdentity))) {
            throw new internal_1.PolymeshError({
                code: types_1.ErrorCode.ValidationError,
                message: 'A MultiSig can only be modified by its admin',
            });
        }
        const rawAddress = (0, conversion_1.stringToAccountId)(multiSig.address, context);
        assertValidRequiredSignatures(newRequiredSignatures);
        assertNoDataChange(requiredSignatures, signersToAdd, signersToRemove, signers, newRequiredSignatures);
        assertRequiredSignersExceedsSigners(currentSignerCount, requiredSignatures, signers, newRequiredSignatures);
        const transactions = [];
        const toRawSignerTx = (signer) => (0, conversion_1.stringToAccountId)(signer.address, context);
        if (newRequiredSignatures) {
            const rawSignersRequired = (0, conversion_1.bigNumberToU64)(newRequiredSignatures, context);
            transactions.push((0, internal_2.checkTxType)({
                transaction: tx.multiSig.changeSigsRequiredViaAdmin,
                args: [rawAddress, rawSignersRequired],
            }));
        }
        if (signersToAdd.length > 0) {
            const rawAddedSigners = signersToAdd.map(signer => toRawSignerTx(signer));
            transactions.push((0, internal_2.checkTxType)({
                transaction: tx.multiSig.addMultisigSignersViaAdmin,
                args: [rawAddress, rawAddedSigners],
            }));
        }
        if (signersToRemove.length > 0) {
            const rawRemovedSigners = signersToRemove.map(signer => toRawSignerTx(signer));
            transactions.push((0, internal_2.checkTxType)({
                transaction: tx.multiSig.removeMultisigSignersViaAdmin,
                args: [rawAddress, rawRemovedSigners],
            }));
        }
        return {
            transactions,
            resolver: undefined,
        };
    });
}
exports.prepareModifyMultiSig = prepareModifyMultiSig;
/**
 * @hidden
 */
function getAuthorization({ requiredSignatures: newRequiredSignatures }) {
    const { storage: { signersToAdd, signersToRemove }, } = this;
    const transactions = [];
    if (signersToAdd.length > 0) {
        transactions.push(types_1.TxTags.multiSig.AddMultisigSignersViaCreator);
    }
    if (signersToRemove.length > 0) {
        transactions.push(types_1.TxTags.multiSig.RemoveMultisigSignersViaCreator);
    }
    if (newRequiredSignatures) {
        transactions.push(types_1.TxTags.multiSig.ChangeSigsRequiredViaCreator);
    }
    return {
        permissions: {
            transactions,
        },
    };
}
exports.getAuthorization = getAuthorization;
/**
 * @hidden
 */
function prepareStorage({ signers, multiSig }) {
    return __awaiter(this, void 0, void 0, function* () {
        const { signers: currentSigners, requiredSignatures } = yield multiSig.details();
        return Object.assign(Object.assign({}, calculateSignerDelta(currentSigners, signers)), { requiredSignatures, currentSignerCount: currentSigners.length });
    });
}
exports.prepareStorage = prepareStorage;
/**
 * @hidden
 */
const modifyMultiSig = () => new internal_1.Procedure(prepareModifyMultiSig, getAuthorization, prepareStorage);
exports.modifyMultiSig = modifyMultiSig;
//# sourceMappingURL=modifyMultiSig.js.map